import React, { useState, useEffect } from 'react';
import { api } from './api';
import './AdminUsers.css';

export default function AdminSections() {
  const [sections, setSections] = useState([]);
  const [filteredSections, setFilteredSections] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    order: 0,
    admin_only: false,
  });

  useEffect(() => {
    loadSections();
  }, []);

  useEffect(() => {
    // Фильтрация разделов по поисковому запросу
    if (!searchQuery.trim()) {
      setFilteredSections(sections);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = sections.filter(section => {
        const title = (section.title || '').toLowerCase();
        const slug = (section.slug || '').toLowerCase();
        return title.includes(query) || slug.includes(query);
      });
      setFilteredSections(filtered);
    }
  }, [searchQuery, sections]);

  const loadSections = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getSections();
      // Если данные приходят в формате пагинации, извлекаем results
      const sections = Array.isArray(data) ? data : (data.results || data);
      setSections(sections);
      setFilteredSections(sections);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (name === 'order' ? parseInt(value) || 0 : value),
    }));
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData(prev => ({
      ...prev,
      title,
      slug: prev.slug || generateSlug(title),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      if (editingSection) {
        await api.updateSection(editingSection.id, formData);
      } else {
        await api.createSection(formData);
      }
      setShowForm(false);
      setEditingSection(null);
      setFormData({
        title: '',
        slug: '',
        order: 0,
        admin_only: false,
      });
      loadSections();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEdit = (section) => {
    setEditingSection(section);
    setFormData({
      title: section.title || '',
      slug: section.slug || '',
      order: section.order || 0,
      admin_only: section.admin_only || false,
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот раздел? Все подкатегории и посты также будут удалены.')) {
      return;
    }

    try {
      await api.deleteSection(id);
      loadSections();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingSection(null);
    setFormData({
      title: '',
      slug: '',
      order: 0,
      admin_only: false,
    });
  };

  if (loading) {
    return <div className="admin-loading">Загрузка разделов...</div>;
  }

  return (
    <div className="admin-users">
      <div className="admin-toolbar">
        <div className="admin-toolbar-actions">
          <div className="admin-search">
            <svg className="admin-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
            <input
              type="text"
              placeholder="Поиск разделов..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="admin-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="admin-search-clear"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            )}
          </div>
          <button className="admin-add-button" onClick={() => setShowForm(true)}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          Добавить раздел
        </button>
        </div>
      </div>

      {error && (
        <div className="admin-error">
          {error}
        </div>
      )}

      {showForm && (
        <div className="admin-form-overlay">
          <form className="admin-form" onSubmit={handleSubmit}>
            <div className="form-header">
              <h4>{editingSection ? 'Редактировать раздел' : 'Создать раздел'}</h4>
              <button type="button" className="form-close" onClick={handleCancel}>×</button>
            </div>

            <div className="form-group">
              <label>Название *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleTitleChange}
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>URL (slug) *</label>
                <input
                  type="text"
                  name="slug"
                  value={formData.slug}
                  onChange={handleInputChange}
                  required
                  pattern="[a-z0-9-]+"
                  title="Только строчные буквы, цифры и дефисы"
                />
              </div>

              <div className="form-group">
                <label>Порядок сортировки</label>
                <input
                  type="number"
                  name="order"
                  value={formData.order}
                  onChange={handleInputChange}
                  min="0"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-checkbox-label">
                <input
                  type="checkbox"
                  name="admin_only"
                  checked={formData.admin_only}
                  onChange={handleInputChange}
                />
                <span>Только для администраторов</span>
              </label>
              <p style={{ marginTop: '4px', fontSize: '12px', color: 'var(--eu-text-muted)' }}>
                Обычные пользователи не увидят этот раздел и посты в нем
              </p>
            </div>

            <div className="form-actions">
              <button type="button" className="admin-button admin-button-cancel" onClick={handleCancel}>
                Отмена
              </button>
              <button type="submit" className="admin-button admin-button-save">
                {editingSection ? 'Сохранить' : 'Создать'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Название</th>
              <th>URL</th>
              <th>Порядок</th>
              <th>Только для админов</th>
              <th>Подкатегорий</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {filteredSections.length === 0 ? (
              <tr>
                <td colSpan="7" className="admin-empty">Нет разделов</td>
              </tr>
            ) : (
              filteredSections.map(section => (
                <tr key={section.id}>
                  <td>{section.id}</td>
                  <td>{section.title}</td>
                  <td><code>{section.slug}</code></td>
                  <td>{section.order}</td>
                  <td>
                    {section.admin_only ? (
                      <span style={{ color: 'var(--eu-accent-strong)', fontWeight: '600' }}>✓ Да</span>
                    ) : (
                      <span style={{ color: 'var(--eu-text-muted)' }}>Нет</span>
                    )}
                  </td>
                  <td>{section.subcategories?.length || 0}</td>
                  <td>
                    <div className="admin-actions">
                      <button
                        className="admin-action-button admin-action-edit"
                        onClick={() => handleEdit(section)}
                        title="Редактировать"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                        </svg>
                      </button>
                      <button
                        className="admin-action-button admin-action-delete"
                        onClick={() => handleDelete(section.id)}
                        title="Удалить"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

